import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import IconButton from '@mui/material/IconButton';
import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { colors, fontSize, fontWeight, TypographyStyledGlobal } from '../../../../theme';
import { HotAlertStatusConfigItem } from '../addSurveyInterface';
import {
    DragHandleStyled,
    StatusColorPreview,
    StatusColorWrapper,
    StatusEditorContainer,
    StatusEditorHeader,
    StatusFieldLabel,
    StatusHexInput,
    StatusInputStyled,
    StatusRadioButton,
    StatusRadioGroup,
    StatusRow,
    StatusRowActions,
    StatusRowContainer,
    StatusRowFields,
} from './styledHotAlertStatusEditor';

interface HotAlertStatusEditorProps {
    statuses: HotAlertStatusConfigItem[];
    onChange: (statuses: HotAlertStatusConfigItem[]) => void;
}

interface SortableStatusRowProps {
    canRemove: boolean;
    onColorChange: (tempId: string, field: 'color' | 'fontColor', value: string) => void;
    onLabelChange: (tempId: string, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onRemove: (tempId: string) => void;
    onSetDefault: (tempId: string) => void;
    onSetFinal: (tempId: string) => void;
    status: HotAlertStatusConfigItem;
}

const SortableStatusRow: FC<SortableStatusRowProps> = ({
    canRemove,
    onColorChange,
    onLabelChange,
    onRemove,
    onSetDefault,
    onSetFinal,
    status,
}) => {
    const { t } = useTranslation('settings');
    const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({ id: status.tempId });

    const style = {
        opacity: isDragging ? 0.5 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999 : undefined,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <StatusRow>
                <DragHandleStyled {...listeners}>
                    <DragIndicatorIcon sx={{ color: colors.grey400, fontSize: 16 }} />
                </DragHandleStyled>

                <StatusRowFields>
                    <StatusInputStyled
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onLabelChange(status.tempId, e)}
                        placeholder={t('tabs.createSurvey.step5Content.statusLabelPlaceholder')}
                        value={status.label}
                    />

                    <StatusRadioGroup>
                        <StatusFieldLabel>{t('tabs.createSurvey.step5Content.statusDefault')}</StatusFieldLabel>
                        <StatusRadioButton
                            checked={status.isDefault}
                            onChange={() => onSetDefault(status.tempId)}
                            type="radio"
                        />
                    </StatusRadioGroup>

                    <StatusRadioGroup>
                        <StatusFieldLabel>{t('tabs.createSurvey.step5Content.statusFinal')}</StatusFieldLabel>
                        <StatusRadioButton
                            checked={status.isFinal}
                            onChange={() => onSetFinal(status.tempId)}
                            type="radio"
                        />
                    </StatusRadioGroup>

                    <StatusColorWrapper>
                        <StatusFieldLabel>{t('tabs.createSurvey.step5Content.statusBgColor')}</StatusFieldLabel>
                        <StatusColorPreview style={{ backgroundColor: status.colors.color }}>
                            <input
                                onChange={(e) => onColorChange(status.tempId, 'color', e.target.value)}
                                type="color"
                                value={status.colors.color}
                            />
                        </StatusColorPreview>
                        <StatusHexInput
                            onChange={(e: ChangeEvent<HTMLInputElement>) => onColorChange(status.tempId, 'color', e.target.value)}
                            placeholder="#000000"
                            value={status.colors.color}
                        />
                    </StatusColorWrapper>

                    <StatusColorWrapper>
                        <StatusFieldLabel>{t('tabs.createSurvey.step5Content.statusFontColor')}</StatusFieldLabel>
                        <StatusColorPreview style={{ backgroundColor: status.colors.fontColor }}>
                            <input
                                onChange={(e) => onColorChange(status.tempId, 'fontColor', e.target.value)}
                                type="color"
                                value={status.colors.fontColor}
                            />
                        </StatusColorPreview>
                        <StatusHexInput
                            onChange={(e: ChangeEvent<HTMLInputElement>) => onColorChange(status.tempId, 'fontColor', e.target.value)}
                            placeholder="#000000"
                            value={status.colors.fontColor}
                        />
                    </StatusColorWrapper>
                </StatusRowFields>

                <StatusRowActions>
                    <IconButton
                        disabled={!canRemove}
                        onClick={() => onRemove(status.tempId)}
                        size="small"
                        sx={{ color: colors.alertDanger }}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </StatusRowActions>
            </StatusRow>
        </div>
    );
};

const createEmptyStatus = (): HotAlertStatusConfigItem => ({
    tempId: `status-${Date.now()}-${Math.random()}`,
    label: '',
    isDefault: false,
    isFinal: false,
    colors: { color: '#E5EAF1', fontColor: '#8993A2' },
});

const HotAlertStatusEditor: FC<HotAlertStatusEditorProps> = ({ statuses, onChange }) => {
    const { t } = useTranslation('settings');

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleAdd = () => {
        onChange([...statuses, createEmptyStatus()]);
    };

    const handleRemove = (tempId: string) => {
        onChange(statuses.filter((s) => s.tempId !== tempId));
    };

    const handleLabelChange = (tempId: string, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange(statuses.map((s) => (s.tempId === tempId ? { ...s, label: e.target.value } : s)));
    };

    const handleSetDefault = (tempId: string) => {
        onChange(statuses.map((s) => ({ ...s, isDefault: s.tempId === tempId })));
    };

    const handleSetFinal = (tempId: string) => {
        onChange(statuses.map((s) => ({ ...s, isFinal: s.tempId === tempId })));
    };

    const handleColorChange = (tempId: string, field: 'color' | 'fontColor', value: string) => {
        onChange(statuses.map((s) => (
            s.tempId === tempId ? { ...s, colors: { ...s.colors, [field]: value } } : s
        )));
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = statuses.findIndex((s) => s.tempId === active.id);
            const newIndex = statuses.findIndex((s) => s.tempId === over.id);
            onChange(arrayMove(statuses, oldIndex, newIndex));
        }
    };

    return (
        <StatusEditorContainer>
            <StatusEditorHeader>
                <TypographyStyledGlobal color={colors.grey200} size={fontSize.sm} weight={fontWeight.regular}>
                    {t('tabs.createSurvey.step5Content.statusSectionTitle')}
                </TypographyStyledGlobal>
                <IconButton onClick={handleAdd} size="small" sx={{ color: colors.default }}>
                    <AddIcon fontSize="small" />
                </IconButton>
            </StatusEditorHeader>

            <StatusRowContainer>
                <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    sensors={sensors}
                >
                    <SortableContext
                        items={statuses.map((s) => s.tempId)}
                        strategy={verticalListSortingStrategy}
                    >
                        {statuses.map((status) => (
                            <SortableStatusRow
                                key={status.tempId}
                                onColorChange={handleColorChange}
                                onLabelChange={handleLabelChange}
                                onRemove={handleRemove}
                                onSetDefault={handleSetDefault}
                                onSetFinal={handleSetFinal}
                                status={status}
                                canRemove
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </StatusRowContainer>
        </StatusEditorContainer>
    );
};

export default HotAlertStatusEditor;
